<div class="audit-logs-page">
  <div class="al-page-heading">
    <div>
      <h1>Audit Logs</h1>
    </div>
    <button mat-stroked-button type="button" (click)="refreshAll()"
      [disabled]="loading || screenshotLoading || settingsLoading">
      <mat-icon>refresh</mat-icon>
      Refresh
    </button>
  </div>

  <div class="al-tabs" role="tablist" aria-label="Audit log type">
    <button type="button" role="tab" class="al-tab" [class.active]="activeTab === 'screencast'"
      [attr.aria-selected]="activeTab === 'screencast'" (click)="setTab('screencast')">
      <mat-icon>screen_share</mat-icon>
      Screencast
    </button>
    <button type="button" role="tab" class="al-tab" [class.active]="activeTab === 'screenshot'"
      [attr.aria-selected]="activeTab === 'screenshot'" (click)="setTab('screenshot')">
      <mat-icon>photo_camera</mat-icon>
      Screenshot
    </button>
    <button *ngIf="settingsTabEnabled" type="button" role="tab" class="al-tab" [class.active]="activeTab === 'settings'"
      [attr.aria-selected]="activeTab === 'settings'" (click)="setTab('settings')">
      <mat-icon>tune</mat-icon>
      Settings
    </button>
  </div>

  <ng-container *ngIf="activeTab === 'screencast'">

    <section class="al-panel al-quota" [class.al-quota--compact]="unlimited && !quotaUnavailable"
      aria-label="Company screencast usage today">
      <div class="al-row">
        <h2>Today’s screencast usage</h2>
        <span class="al-plan" *ngIf="!quotaUnavailable">{{ unlimited ? 'Unlimited' : (quotaAllowanceLabel(false,
          limitMinutes) + ' / day') }}</span>
      </div>

      <!-- Unlimited: one quiet strip -->
      <div class="al-quota-compact" *ngIf="unlimited && !quotaUnavailable">
        <div class="al-quota-compact-stat">
          <span class="al-stat-label">Used today</span>
          <span class="al-stat-value primary">{{ formatConnectedDuration(usedSeconds) }}</span>
        </div>
        <span class="al-muted al-small">{{ resetLabel }}</span>
      </div>

      <!-- Limited / unavailable: Used · Allowance · Remaining -->
      <ng-container *ngIf="!unlimited || quotaUnavailable">
        <div class="al-stats">
          <div>
            <div class="al-stat-label">Used today</div>
            <div class="al-stat-value primary">{{ quotaUnavailable ? '—' : formatConnectedDuration(usedSeconds) }}
            </div>
          </div>
          <div>
            <div class="al-stat-label">Daily allowance</div>
            <div class="al-stat-value">{{ quotaUnavailable ? '—' : quotaAllowanceLabel(unlimited, limitMinutes) }}
            </div>
          </div>
          <div>
            <div class="al-stat-label">Remaining today</div>
            <div class="al-stat-value">{{ quotaUnavailable ? '—' :
              formatConnectedDuration(remainingSeconds) }}</div>
          </div>
        </div>
        <div class="al-track" *ngIf="!unlimited && !quotaUnavailable" role="progressbar"
          aria-label="Daily company screencast allowance used" [attr.aria-valuenow]="quotaPercent" aria-valuemin="0"
          aria-valuemax="100">
          <div class="al-fill" [style.width.%]="quotaPercent" [class.exhausted]="exhausted"></div>
        </div>
        <div class="al-row al-small al-muted">
          <span *ngIf="quotaUnavailable && !quotaStale">Usage unavailable right now</span>
          <span *ngIf="quotaStale">Showing last successful update · {{ lastUpdateLabel }}</span>
          <span *ngIf="!quotaUnavailable && !quotaStale">{{ quotaPercent }}% used</span>
          <span *ngIf="!quotaUnavailable">{{ resetLabel }}</span>
        </div>
      </ng-container>

      <div class="al-warning" *ngIf="nearLimit && !exhausted">Only {{ formatConnectedDuration(remainingSeconds) }} left
        today. Simultaneous streams use the allowance faster.</div>
      <div class="al-warning exhausted" *ngIf="exhausted">
        <strong>Daily limit reached.</strong> Viewing is paused until the reset.
      </div>
      <div class="al-warning" *ngIf="quotaStale">Couldn’t refresh usage. Showing the last successful numbers.</div>
    </section>

    <div class="al-row al-log-heading">
      <h2>Viewing sessions <span class="al-muted al-small" *ngIf="!loading && !sessionsError">· {{ total }}</span></h2>
    </div>

    <section class="al-panel" aria-label="Screencast sessions">
      <form [formGroup]="filterForm" class="al-filters">
        <div class="al-filter-item">
          <label class="al-filter-label">Date range</label>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-select formControlName="datePreset">
              <mat-option value="today">Today</mat-option>
              <mat-option value="yesterday">Yesterday</mat-option>
              <mat-option value="week">Last 7 days</mat-option>
              <mat-option value="month">Last 30 days</mat-option>
              <mat-option value="custom">Custom range</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="al-filter-item al-filter-item--employee">
          <label class="al-filter-label">Employee</label>
          <app-employee-search [userList]="employees" [placeholder]="'All Employees'" [isShowAllEmp]="true"
            class="al-employee-search w-full h-full" [selectedEmployeeId]="filterForm.get('employee_user_id')?.value"
            (employeeSelected)="onEmployeeSelected($event)">
          </app-employee-search>
        </div>

        <div class="al-filter-item">
          <label class="al-filter-label">Requested by</label>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-select formControlName="admin_user_id">
              <mat-option value="">All requesters</mat-option>
              <mat-option *ngFor="let user of requesters; trackBy: trackByUserId" [value]="user.id">{{ user.name
                }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="al-filter-item">
          <label class="al-filter-label">Mode</label>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-select formControlName="mode">
              <mat-option *ngFor="let mode of modes" [value]="mode">{{ mode ? displayMode(mode) : 'All modes'
                }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="al-filter-item">
          <label class="al-filter-label">Status</label>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-select formControlName="status">
              <mat-option *ngFor="let status of statuses" [value]="status">{{ status || 'All statuses' }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </form>

      <form class="al-custom-dates" *ngIf="showCustomDates" (ngSubmit)="applyCustomDates()" novalidate>
        <mat-form-field appearance="outline">
          <mat-label>Start date</mat-label>
          <input matInput type="date" [(ngModel)]="customStart" name="customStart" required>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>End date</mat-label>
          <input matInput type="date" [(ngModel)]="customEnd" name="customEnd" required>
        </mat-form-field>
        <button mat-flat-button color="primary" type="submit">Apply dates</button>
        <button mat-stroked-button type="button" (click)="cancelCustomDates()">Cancel</button>
        <p class="al-date-error" *ngIf="dateError" role="alert">{{ dateError }}</p>
      </form>
      <div class="al-range-bar">
        <span class="al-range-caption" aria-live="polite">{{ appliedRangeLabel }}</span>
        <button type="button" class="al-clear-filters-btn" *ngIf="hasActiveFilters"
          (click)="clearFilters()">Clear filters</button>
      </div>

      <div *ngIf="loading" class="al-state" role="status">
        Loading screencast sessions…
        <div class="al-skeleton"></div>
        <div class="al-skeleton" style="width:75%"></div>
        <div class="al-skeleton"></div>
      </div>

      <div *ngIf="!loading && sessionsError" class="al-state" role="alert">
        <h3>Unable to load screencast logs</h3>
        <p>Your viewing history is temporarily unavailable. Quota is not shown as zero or unlimited.</p>
        <button mat-stroked-button type="button" (click)="refreshAll()">Try again</button>
      </div>

      <div *ngIf="!loading && !sessionsError && sessions.length === 0" class="al-state">
        <h3>{{ appliedPreset === 'today' && !filterForm.value.employee_user_id && !filterForm.value.status ? 'No viewing
          sessions today' : 'No sessions match your filters' }}</h3>
        <p>{{ appliedPreset === 'today' && !filterForm.value.employee_user_id && !filterForm.value.status ? 'Screencast
          activity will appear here after a viewing request.' : 'Try another employee, date range, or status.' }}</p>
        <button mat-stroked-button type="button"
          *ngIf="filterForm.value.employee_user_id || filterForm.value.status || appliedPreset !== 'today'"
          (click)="clearFilters()">Clear filters</button>
      </div>

      <ng-container *ngIf="!loading && !sessionsError && sessions.length > 0">
        <div class="al-table-scroll">
          <table class="al-table" aria-label="Screencast sessions">
            <thead>
              <tr class="table-head">
                <th class="index-cell">#</th>
                <th class="user-profile-cell">Employee viewed</th>
                <th>Requested by</th>
                <th>Mode</th>
                <th>When</th>
                <th>Duration</th>
                <th>Status / end reason</th>
                <th class="al-actions-cell" aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let session of sessions; let i = index; trackBy: trackBySession"
                class="user-list-row transition-colors">
                <td class="index-cell">{{ (page - 1) * pageSize + i + 1 }}</td>
                <td class="user-profile-cell">
                  <user-profile [user]="getUserForProfile(session.employee)" [sizeAvatar]="34"
                    [userImageClass]="'avatar-img'"></user-profile>
                </td>
                <td class="requester-cell font-medium text-gray-800">{{ personName(session.requester) }}</td>
                <td class="al-mode">
                  <span class="al-mode-pill">{{ displayMode(session.mode) }}</span>
                </td>
                <td>
                  <div class="al-when">
                    <span class="font-medium text-gray-800">{{ dateLine(session.requested_at_utc) }}</span>
                    <span class="al-muted al-small tabular-nums">{{ sessionTimeRange(session) }}</span>
                    <span class="al-midnight-badge" *ngIf="session.completeness?.crossed_midnight">Crossed
                      midnight</span>
                  </div>
                </td>
                <td>
                  <span class="font-semibold text-gray-800 tabular-nums">{{ durationLabel(session) }}</span>
                  <div class="al-muted al-small" *ngIf="durationNote(session)">{{ durationNote(session) }}</div>
                </td>
                <td>
                  <span class="al-status" [ngClass]="statusClass(session.status)">{{ session.status }}</span>
                  <div class="al-end-reason al-muted al-small mt-0.5"
                    *ngIf="readableEndReason(session.end_reason, session.status) !== '—'">
                    {{ readableEndReason(session.end_reason, session.status) }}
                  </div>
                </td>
                <td class="al-actions-cell">
                  <button type="button" class="al-copy-id"
                    [class.copied]="copiedSessionId === session.session_id"
                    (click)="copySessionId(session.session_id)"
                    [matTooltip]="copiedSessionId === session.session_id ? 'Copied' : 'Copy session ID'"
                    [attr.aria-label]="copiedSessionId === session.session_id ? 'Session ID copied' : 'Copy session ID'">
                    <mat-icon>{{ copiedSessionId === session.session_id ? 'check' : 'content_copy' }}</mat-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="al-mobile-list">
          <article class="al-mobile-session" *ngFor="let session of sessions; trackBy: trackBySession">
            <div class="al-row">
              <user-profile [user]="getUserForProfile(session.employee)" [sizeAvatar]="32"
                [userImageClass]="'avatar-img'"></user-profile>
              <div class="al-row al-mobile-status">
                <span class="al-status" [ngClass]="statusClass(session.status)">{{ session.status }}</span>
                <button type="button" class="al-copy-id al-copy-id--always"
                  [class.copied]="copiedSessionId === session.session_id"
                  (click)="copySessionId(session.session_id)"
                  [matTooltip]="copiedSessionId === session.session_id ? 'Copied' : 'Copy session ID'"
                  [attr.aria-label]="copiedSessionId === session.session_id ? 'Session ID copied' : 'Copy session ID'">
                  <mat-icon>{{ copiedSessionId === session.session_id ? 'check' : 'content_copy' }}</mat-icon>
                </button>
              </div>
            </div>
            <div class="al-mobile-meta">
              <span>{{ personName(session.requester) }} · {{ displayMode(session.mode) }}</span>
              <span>{{ dateLine(session.requested_at_utc) }} · {{ sessionTimeRange(session) }}</span>
              <span>{{ durationLabel(session) }} {{ durationNote(session) }}</span>
              <span class="al-end-reason">{{ readableEndReason(session.end_reason, session.status) }}</span>
            </div>
          </article>
        </div>

        <div class="al-table-footer">
          <span class="al-footer-count">{{ total }} session{{ total !== 1 ? 's' : '' }} overlapping the selected dates
            in {{ timezone }}</span>
          <mat-paginator [length]="total" [pageSize]="pageSize" [pageIndex]="page - 1"
            [pageSizeOptions]="pageSizeOptions" aria-label="Select screencast page" (page)="onPaginateChange($event)">
          </mat-paginator>
        </div>
      </ng-container>
    </section>
  </ng-container>

  <ng-container *ngIf="activeTab === 'screenshot'">
    <div class="al-row al-log-heading">
      <h2>Screenshot activity <span class="al-muted al-small" *ngIf="!screenshotLoading && !screenshotError">· {{
          screenshotTotal }}</span></h2>
    </div>

    <section class="al-panel" aria-label="Screenshot audit logs">
      <form [formGroup]="filterForm" class="al-filters al-filters-4">
        <div class="al-filter-item">
          <label class="al-filter-label">Date range</label>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-select formControlName="datePreset">
              <mat-option value="today">Today</mat-option>
              <mat-option value="yesterday">Yesterday</mat-option>
              <mat-option value="week">Last 7 days</mat-option>
              <mat-option value="month">Last 30 days</mat-option>
              <mat-option value="custom">Custom range</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="al-filter-item al-filter-item--employee">
          <label class="al-filter-label">Employee</label>
          <app-employee-search [userList]="employees" [placeholder]="'All Employees'" [isShowAllEmp]="true"
            class="al-employee-search w-full h-full" [selectedEmployeeId]="filterForm.get('employee_user_id')?.value"
            (employeeSelected)="onEmployeeSelected($event)">
          </app-employee-search>
        </div>

        <div class="al-filter-item">
          <label class="al-filter-label">Requested / deleted by</label>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-select formControlName="admin_user_id">
              <mat-option value="">All actors</mat-option>
              <mat-option *ngFor="let user of requesters; trackBy: trackByUserId" [value]="user.id">{{ user.name
                }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="al-filter-item">
          <label class="al-filter-label">Action</label>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-select formControlName="event_type">
              <mat-option value="">All actions</mat-option>
              <mat-option value="on_demand_request">On demand</mat-option>
              <mat-option value="screenshot_delete">Delete</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </form>

      <form class="al-custom-dates" *ngIf="showCustomDates" (ngSubmit)="applyCustomDates()" novalidate>
        <mat-form-field appearance="outline">
          <mat-label>Start date</mat-label>
          <input matInput type="date" [(ngModel)]="customStart" name="ssCustomStart" required>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>End date</mat-label>
          <input matInput type="date" [(ngModel)]="customEnd" name="ssCustomEnd" required>
        </mat-form-field>
        <button mat-flat-button color="primary" type="submit">Apply dates</button>
        <button mat-stroked-button type="button" (click)="cancelCustomDates()">Cancel</button>
        <p class="al-date-error" *ngIf="dateError" role="alert">{{ dateError }}</p>
      </form>
      <div class="al-range-bar">
        <span class="al-range-caption" aria-live="polite">{{ appliedRangeLabel }}</span>
        <button type="button" class="al-clear-filters-btn" *ngIf="hasActiveFilters"
          (click)="clearFilters()">Clear filters</button>
      </div>

      <div *ngIf="screenshotLoading" class="al-state" role="status">
        Loading screenshot activity…
        <div class="al-skeleton"></div>
        <div class="al-skeleton" style="width:75%"></div>
        <div class="al-skeleton"></div>
      </div>

      <div *ngIf="!screenshotLoading && screenshotError" class="al-state" role="alert">
        <h3>Unable to load screenshot logs</h3>
        <p>On-demand requests and deletes are temporarily unavailable.</p>
        <button mat-stroked-button type="button" (click)="refreshAll()">Try again</button>
      </div>

      <div *ngIf="!screenshotLoading && !screenshotError && screenshotEvents.length === 0" class="al-state">
        <h3>{{ appliedPreset === 'today' && !filterForm.value.employee_user_id && !filterForm.value.event_type ? 'No
          screenshot activity today' : 'No activity matches your filters' }}</h3>
        <p>{{ appliedPreset === 'today' && !filterForm.value.employee_user_id && !filterForm.value.event_type ?
          'On-demand requests and screenshot deletes will appear here.' : 'Try another employee, date range, or action.'
          }}</p>
        <button mat-stroked-button type="button"
          *ngIf="filterForm.value.employee_user_id || filterForm.value.event_type || appliedPreset !== 'today'"
          (click)="clearFilters()">Clear filters</button>
      </div>

      <ng-container *ngIf="!screenshotLoading && !screenshotError && screenshotEvents.length > 0">
        <div class="al-table-scroll">
          <table class="al-table" aria-label="Screenshot audit logs">
            <thead>
              <tr class="table-head">
                <th class="index-cell">#</th>
                <th class="user-profile-cell">Employee</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Time</th>
                <th>Source</th>
                <th>Screenshot</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let event of screenshotEvents; let i = index; trackBy: trackByScreenshotEvent"
                class="user-list-row transition-colors">
                <td class="index-cell">{{ (screenshotPage - 1) * pageSize + i + 1 }}</td>
                <td class="user-profile-cell">
                  <user-profile [user]="getUserForProfile(event.employee)" [sizeAvatar]="34"
                    [userImageClass]="'avatar-img'"></user-profile>
                </td>
                <td>
                  <div class="font-medium text-gray-800">{{ personName(event.actor) }}</div>
                  <div class="al-muted al-small" *ngIf="actorRoleLabel(event.actor_role)">{{
                    actorRoleLabel(event.actor_role) }}</div>
                </td>
                <td>
                  <span class="al-status" [ngClass]="event.event_type === 'screenshot_delete' ? 'failed' : 'live'">
                    {{ event.action || screenshotActionLabel(event.event_type) }}
                  </span>
                </td>
                <td>
                  <div class="al-when">
                    <span class="font-medium text-gray-800">{{ dateLine(event.created_at_utc) }}</span>
                    <span class="al-muted al-small">{{ timeLine(event.created_at_utc) }}</span>
                  </div>
                </td>
                <td>
                  <span class="al-source-badge">{{ screenshotSourceLabel(event.source) }}</span>
                </td>
                <td>
                  <button type="button" class="al-thumb"
                    *ngIf="event.event_type === 'on_demand_request' && event.screenshot_id && event.image_url"
                    (click)="openScreenshot(event)" [attr.aria-label]="'Enlarge screenshot ' + event.screenshot_id">
                    <img [src]="event.image_url" alt="">
                  </button>
                  <span class="al-muted text-xs"
                    *ngIf="event.event_type === 'on_demand_request' && !event.screenshot_id">Not
                    captured</span>
                  <span class="text-xs font-mono text-gray-600"
                    *ngIf="event.event_type === 'on_demand_request' && event.screenshot_id && !event.image_url">#{{
                    event.screenshot_id }}</span>
                  <div *ngIf="event.event_type === 'screenshot_delete'">
                    <div class="al-muted al-small font-mono" *ngIf="event.screenshot_id">#{{ event.screenshot_id }}
                    </div>
                    <div class="text-xs text-gray-700">{{ event.screenshot_ist_time ?
                      capturedTimeLabel(event.screenshot_ist_time) : '—' }}</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="al-mobile-list">
          <article class="al-mobile-session" *ngFor="let event of screenshotEvents; trackBy: trackByScreenshotEvent">
            <div class="al-row">
              <user-profile [user]="getUserForProfile(event.employee)" [sizeAvatar]="32"
                [userImageClass]="'avatar-img'"></user-profile>
              <span class="al-status" [ngClass]="event.event_type === 'screenshot_delete' ? 'failed' : 'live'">
                {{ event.action || screenshotActionLabel(event.event_type) }}
              </span>
            </div>
            <div class="al-mobile-meta">
              <span>{{ personName(event.actor) }} · {{ actorRoleLabel(event.actor_role) }}</span>
              <span>{{ clock(event.created_at_utc) }}</span>
              <span>{{ screenshotSourceLabel(event.source) }}</span>
              <button type="button" class="al-thumb"
                *ngIf="event.event_type === 'on_demand_request' && event.screenshot_id && event.image_url"
                (click)="openScreenshot(event)" [attr.aria-label]="'Enlarge screenshot ' + event.screenshot_id">
                <img [src]="event.image_url" alt="">
              </button>
              <span *ngIf="event.event_type === 'on_demand_request' && !event.screenshot_id">Not captured</span>
              <span *ngIf="event.event_type === 'screenshot_delete'">Removed shot {{ event.screenshot_ist_time ?
                capturedTimeLabel(event.screenshot_ist_time) : '—' }}</span>
            </div>
          </article>
        </div>

        <div class="al-table-footer">
          <span class="al-footer-count">{{ screenshotTotal }} event{{ screenshotTotal !== 1 ? 's' : '' }} in {{ timezone
            }}</span>
          <mat-paginator [length]="screenshotTotal" [pageSize]="pageSize" [pageIndex]="screenshotPage - 1"
            [pageSizeOptions]="pageSizeOptions" aria-label="Select screenshot page"
            (page)="onScreenshotPaginateChange($event)">
          </mat-paginator>
        </div>
      </ng-container>
    </section>
  </ng-container>

  <ng-container *ngIf="activeTab === 'settings'">
    <div class="al-row al-log-heading">
      <h2>Settings changes <span class="al-muted al-small" *ngIf="!settingsLoading && !settingsError">· {{
          settingsTotal }}</span></h2>
    </div>

    <section class="al-panel" aria-label="Settings audit logs">
      <form [formGroup]="filterForm" class="al-filters al-filters-4">
        <div class="al-filter-item">
          <label class="al-filter-label">Date range</label>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-select formControlName="datePreset">
              <mat-option value="today">Today</mat-option>
              <mat-option value="yesterday">Yesterday</mat-option>
              <mat-option value="week">Last 7 days</mat-option>
              <mat-option value="month">Last 30 days</mat-option>
              <mat-option value="custom">Custom range</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="al-filter-item al-filter-item--employee">
          <label class="al-filter-label">Employee affected</label>
          <app-employee-search [userList]="employees" [placeholder]="'All Employees'" [isShowAllEmp]="true"
            class="al-employee-search w-full h-full" [selectedEmployeeId]="filterForm.get('employee_user_id')?.value"
            (employeeSelected)="onEmployeeSelected($event)">
          </app-employee-search>
        </div>

        <div class="al-filter-item">
          <label class="al-filter-label">Changed by</label>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-select formControlName="admin_user_id">
              <mat-option value="">All actors</mat-option>
              <mat-option *ngFor="let user of requesters; trackBy: trackByUserId" [value]="user.id">{{ user.name
                }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </form>

      <form class="al-custom-dates" *ngIf="showCustomDates" (ngSubmit)="applyCustomDates()" novalidate>
        <mat-form-field appearance="outline">
          <mat-label>Start date</mat-label>
          <input matInput type="date" [(ngModel)]="customStart" name="settingsCustomStart" required>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>End date</mat-label>
          <input matInput type="date" [(ngModel)]="customEnd" name="settingsCustomEnd" required>
        </mat-form-field>
        <button mat-flat-button color="primary" type="submit">Apply dates</button>
        <button mat-stroked-button type="button" (click)="cancelCustomDates()">Cancel</button>
        <p class="al-date-error" *ngIf="dateError" role="alert">{{ dateError }}</p>
      </form>
      <div class="al-range-bar">
        <span class="al-range-caption" aria-live="polite">{{ appliedRangeLabel }}</span>
        <button type="button" class="al-clear-filters-btn" *ngIf="hasActiveFilters"
          (click)="clearFilters()">Clear filters</button>
      </div>

      <div *ngIf="settingsLoading" class="al-state" role="status">
        Loading settings changes…
        <div class="al-skeleton"></div>
        <div class="al-skeleton" style="width:75%"></div>
        <div class="al-skeleton"></div>
      </div>

      <div *ngIf="!settingsLoading && settingsError" class="al-state" role="alert">
        <h3>Unable to load settings logs</h3>
        <p>Settings change history is temporarily unavailable.</p>
        <button mat-stroked-button type="button" (click)="refreshAll()">Try again</button>
      </div>

      <div *ngIf="!settingsLoading && !settingsError && settingsEvents.length === 0" class="al-state">
        <h3>{{ !hasActiveFilters
          ? 'No settings changes in this range' : 'No activity matches your filters' }}</h3>
        <p>{{ !hasActiveFilters
          ? 'Company, employee, and bulk settings saves will appear here.' : 'Try another employee, actor, or date range.' }}</p>
        <button mat-stroked-button type="button"
          *ngIf="hasActiveFilters"
          (click)="clearFilters()">Clear filters</button>
      </div>

      <ng-container *ngIf="!settingsLoading && !settingsError && settingsEvents.length > 0">
        <div class="al-table-scroll">
          <table class="al-table" aria-label="Settings audit logs">
            <thead>
              <tr class="table-head">
                <th class="index-cell">#</th>
                <th>Changed by</th>
                <th>Action</th>
                <th>Employees</th>
                <th>Settings</th>
                <th>Time</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <ng-container *ngFor="let event of settingsEvents; let i = index; trackBy: trackBySettingsEvent">
                <tr class="user-list-row transition-colors">
                  <td class="index-cell">{{ (settingsPage - 1) * pageSize + i + 1 }}</td>
                  <td>
                    <div class="font-medium text-gray-800">{{ personName(event.actor) }}</div>
                  </td>
                  <td>
                    <span class="al-status live">{{ event.action || 'Bulk update' }}</span>
                  </td>
                  <td>
                    <div class="font-medium text-gray-800">{{ settingsEmployeesPreview(event) }}</div>
                    <div class="al-muted al-small" *ngIf="event.employee_count > 0">{{ event.employee_count }} employee{{ event.employee_count
                      !== 1 ? 's' : '' }}</div>
                  </td>
                  <td>
                    <div class="al-muted al-small">{{ settingsSummaryPreview(event) }}</div>
                  </td>
                  <td>
                    <div class="al-when">
                      <span class="font-medium text-gray-800">{{ dateLine(event.updated_at_utc) }}</span>
                      <span class="al-muted al-small">{{ timeLine(event.updated_at_utc) }}</span>
                    </div>
                  </td>
                  <td>
                    <button type="button" class="al-clear-filters-btn" (click)="toggleSettingsDetails(event.id)">
                      {{ expandedSettingsId === event.id ? 'Hide' : 'Details' }}
                    </button>
                  </td>
                </tr>
                <tr *ngIf="expandedSettingsId === event.id" class="al-settings-detail-row">
                  <td colspan="7">
                    <div class="al-settings-detail">
                      <div *ngIf="event.employees?.length">
                        <div class="al-stat-label">Employees</div>
                        <div class="al-settings-chips">
                          <span class="al-source-badge" *ngFor="let person of event.employees; trackBy: trackByUserId">{{
                            personName(person) }}</span>
                        </div>
                      </div>
                      <div>
                        <div class="al-stat-label">{{ event.action_type === 'bulk_update' || event.scope === 'bulk' ? 'Applied settings' : 'Changes' }}</div>
                        <div class="al-settings-chips">
                          <span class="al-source-badge" *ngFor="let row of event.settings_summary">{{ settingsDiffLabel(row) }}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </div>

        <div class="al-mobile-list">
          <article class="al-mobile-session" *ngFor="let event of settingsEvents; trackBy: trackBySettingsEvent">
            <div class="al-row">
              <div>
                <div class="font-medium text-gray-800">{{ personName(event.actor) }}</div>
                <div class="al-muted al-small">{{ settingsEmployeesPreview(event) }}</div>
              </div>
              <span class="al-status live">{{ event.action || 'Bulk update' }}</span>
            </div>
            <div class="al-mobile-meta">
              <span>{{ clock(event.updated_at_utc) }}</span>
              <span>{{ settingsSummaryPreview(event, 2) }}</span>
              <button type="button" class="al-clear-filters-btn" (click)="toggleSettingsDetails(event.id)">
                {{ expandedSettingsId === event.id ? 'Hide details' : 'Show details' }}
              </button>
              <div class="al-settings-detail" *ngIf="expandedSettingsId === event.id">
                <div class="al-settings-chips">
                  <span class="al-source-badge" *ngFor="let row of event.settings_summary">{{ settingsDiffLabel(row) }}</span>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div class="al-table-footer">
          <span class="al-footer-count">{{ settingsTotal }} change{{ settingsTotal !== 1 ? 's' : '' }} in {{ timezone
            }}</span>
          <mat-paginator [length]="settingsTotal" [pageSize]="pageSize" [pageIndex]="settingsPage - 1"
            [pageSizeOptions]="pageSizeOptions" aria-label="Select settings page"
            (page)="onSettingsPaginateChange($event)">
          </mat-paginator>
        </div>
      </ng-container>
    </section>
  </ng-container>
</div>